import React from 'react';

import {
  Breakpoints,
  Clock,
  CTA,
  Divider,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';
import { Alert } from '@volkswagen-onehub/icons-core';

import { AmbientLight } from '../../../icons/AmbientLight';
import { DiscoverySvg } from '../../../icons/DiscoverySvg';
import {
  MaintenanceSchedulePackagesData,
  TimeBasedServices,
} from '../../../typings/general';
import { ServicesLayer } from '../ServicesLayer/ServicesLayer';
import {
  StyledAlertWrapper,
  StyledContent,
  StyledContentSection,
  StyledErrorAPIContent,
  StyledErrorAPITitle,
  StyledInformation,
  StyledInformationDescription,
  StyledInformationTitle,
  StyledMaintenanceType,
  StyledMaintenanceTypeTitle,
  StyledViewAllCTA,
} from './styled';
import {
  onVWBasicLayerLoad,
  onVWBasicLinkClickEvent,
} from '../../../tagging/tagging-helpers';
import { useFeatureAppConfig } from '../../../hooks/use-feature-app-config';
import { useMaintenanceContext } from '../../../hooks/use-maintenance-context';
import { useTrackingManager } from '../../../hooks/use-tracking-manager';
import { useViewport, ViewportType } from '../../../hooks/use-viewport';
import { viewAllRecommendedServices } from '../../../utils/maintenance-schedule-utils';
import { useStore } from '../../../hooks-store/store';
import { parseMileageNumber } from '../../../utils/general';

interface ContentProps {
  additionalServices: TimeBasedServices[];
  isLastMaintenance: boolean | undefined;
  onViewAllServices: (value: boolean) => void;
  packages?: MaintenanceSchedulePackagesData;
  viewCompleteSchedule: boolean;
  showTitle?: boolean;
  nextMileage?: string;
}

const TitleText = ({ children }) => (
  <TextCustom
    appearance={TextAppearance.headline200}
    tag={TextTag.p}
    style={{
      [Breakpoints.default]: {
        fontSize: TextSize.TextSize20,
        fontWeight: TextWeight.bold,
        lineHeight: TextLineHeight.TextLineHeight24,
      },
      [Breakpoints.b1920]: {
        fontSize: TextSize.TextSize24,
        fontWeight: TextWeight.bold,
        lineHeight: TextLineHeight.TextLineHeight28,
      },
    }}
  >
    {children}
  </TextCustom>
);

const BodyText = ({ children }) => (
  <TextCustom
    appearance={TextAppearance.copy100}
    tag={TextTag.p}
    style={{
      [Breakpoints.default]: {
        fontSize: TextSize.TextSize14,
        fontWeight: TextWeight.regular,
        lineHeight: TextLineHeight.TextLineHeight24,
      },
      [Breakpoints.b1920]: {
        fontSize: TextSize.TextSize16,
        fontWeight: TextWeight.regular,
        lineHeight: TextLineHeight.TextLineHeight24,
      },
    }}
  >
    {children}
  </TextCustom>
);

const ButtonText = ({ children }) => (
  <TextCustom
    appearance={TextAppearance.label200}
    tag={TextTag.p}
    style={{
      [Breakpoints.default]: {
        fontSize: TextSize.TextSize16,
        fontWeight: TextWeight.regular,
        lineHeight: TextLineHeight.TextLineHeight20,
      },
    }}
  >
    {children}
  </TextCustom>
);

const InformationSection = ({
  icon: Icon,
  title,
  description,
  extraContent = undefined,
}: {
  icon: any;
  title: string;
  description: string;
  extraContent?: React.ReactNode;
}) => (
  <>
    <Divider />
    <StyledInformation>
      <StyledInformationTitle>
        <Icon aria-hidden="true" />
        <BodyText>{title}</BodyText>
      </StyledInformationTitle>
      <StyledInformationDescription>
        <BodyText>
          {description} {extraContent && extraContent}
        </BodyText>
      </StyledInformationDescription>
    </StyledInformation>
  </>
);

export const Content = ({
  packages,
  viewCompleteSchedule,
  additionalServices,
  onViewAllServices,
  isLastMaintenance,
  showTitle = true,
  nextMileage,
}: ContentProps) => {
  const { mode } = useFeatureAppConfig();
  const { translationHandler } = useMaintenanceContext();
  const trackingManager = useTrackingManager();
  const [store] = useStore();

  const viewport = useViewport(Breakpoints.b560, Breakpoints.b1920);
  const isDesktop: boolean = viewport === ViewportType.DESKTOP;

  const formattedNextMileage = nextMileage
    ? Number(nextMileage).toLocaleString()
    : '';

  const shouldShowViewAllButton =
    !viewCompleteSchedule ||
    (packages && Object.entries(packages.formattedNotes).length === 0) ||
    (viewAllRecommendedServices(packages!.formattedNotes, isDesktop) &&
      viewCompleteSchedule);

  const handleViewAllServicesTracking = () => {
    const link = {
      name: translationHandler('maintenance.view.all'),
      url: window.location.href
    }

    onVWBasicLinkClickEvent(
      trackingManager,
      {
        Type: 'Link',
        Name: translationHandler('maintenance.view.all'),
        ItemCount: 1,
        ItemPosition: 1,
      },
      'Link',
      undefined, // document data if needed
      undefined, // additional tracking data
      mode,
      link
    );
    const vehicle: any = {
      modelName: store.currentData.model,
      modelYear: store.currentData.modelYear,
      trim: store.currentData.trim,
      vin: store.currentData.vin,
      odometer: parseMileageNumber(store.form.mileage)
    }

    onVWBasicLayerLoad(trackingManager, mode, vehicle);
  };

  return packages?.label ? (
    <StyledContent data-testid="info-card-content">
      <StyledMaintenanceType>
        <StyledContentSection>
          {Object.entries(packages.formattedNotes).length > 0 && (
            <StyledMaintenanceTypeTitle
              viewCompleteSchedule={viewCompleteSchedule}
            >
              <TitleText>{packages.label ?? ''}</TitleText>
              <BodyText>{packages.description}</BodyText>
            </StyledMaintenanceTypeTitle>
          )}

          {Object.entries(packages.formattedNotes).length === 0 && (
            <StyledErrorAPIContent>
              <StyledErrorAPITitle>
                <StyledAlertWrapper>
                  <Alert />
                </StyledAlertWrapper>
                <TextCustom
                  appearance={TextAppearance.headline300}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize12,
                      fontWeight: TextWeight.regular,
                      lineHeight: TextLineHeight.TextLineHeight20,
                      color: TextColor.active,
                    },
                    [Breakpoints.b1280]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.regular,
                      lineHeight: TextLineHeight.TextLineHeight24,
                      color: TextColor.active,
                    },
                    [Breakpoints.b1920]: {
                      fontSize: TextSize.TextSize16,
                      fontWeight: TextWeight.regular,
                      lineHeight: TextLineHeight.TextLineHeight24,
                      color: TextColor.active,
                    },
                  }}
                >
                  {translationHandler('maintenance.default.not.found')}
                </TextCustom>
              </StyledErrorAPITitle>
            </StyledErrorAPIContent>
          )}

          {packages.frequency &&
            (!isLastMaintenance ||
              (isLastMaintenance && viewCompleteSchedule)) && (
              <InformationSection
                icon={Clock}
                title={packages.frequency}
                description={packages.frequencyDescription}
              />
            )}

          {Object.entries(packages.formattedNotes).length > 0 && (
            <InformationSection
              icon={AmbientLight}
              title={translationHandler('maintenance.expect')}
              description={packages.whatToExpect}
            />
          )}

          {isLastMaintenance && (
            <InformationSection
              icon={DiscoverySvg}
              description={translationHandler('maintenance.minor.whats.next')}
              title={translationHandler('maintenance.next').replace(
                'XXXk',
                formattedNextMileage ? `${formattedNextMileage}` : '',
              )}
              extraContent={
                <CTA
                  aria-label="Go to offers CTA"
                  emphasis="tertiary"
                  href="https://www.vw.com/en/offers.html"
                  tag="a"
                  target="_blank"
                >
                  {translationHandler('maintenance.explore.offers.label')}
                </CTA>
              }
            />
          )}

          <Divider />

          {(viewCompleteSchedule ||
            Object.entries(packages.formattedNotes).length === 0) && (
              <ServicesLayer
                packages={packages}
                additionalServices={additionalServices}
                showAllServices={false}
                onViewAllServices={onViewAllServices}
                handleViewAllServicesTracking={handleViewAllServicesTracking}
                shouldShowViewAllButton={shouldShowViewAllButton}
                showTitle={showTitle}
              />
            )}
        </StyledContentSection>

        {shouldShowViewAllButton && (
          <StyledViewAllCTA>
            <CTA
              tag="button"
              emphasis="tertiary"
              onClick={() => {
                onViewAllServices(true);
                handleViewAllServicesTracking();
              }}
            >
              <ButtonText>
                {translationHandler('maintenance.view.all')}
              </ButtonText>
            </CTA>
          </StyledViewAllCTA>
        )}
      </StyledMaintenanceType>
    </StyledContent>
  ) : (
    <></>
  );
};
